import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { ApiResponse } from "../../../api/apiResponse";
import {
  createCandidate,
  updateCandidateApi,
  removeCandidateApi,
} from "../../../api/endpoints/liaisons/candidates";
import CandidateModel from "../../../api/models/CandidateModel";
import Modal from "../../../components/modal/ModalWithFooter";
import { classNames, showApiErrorMessages } from "../../../helpers";
import AppInput from "../../forms/AppInput";
import DropDown from "../../forms/dropdown/DropDown";
import PhoneNumberInput from "../../forms/phone-input/PhoneNumberInput";
import LoadingSpinner from "../../LoadingSpinner";
import FooterModelButton from "../../buttons/FooterModelButton";
type AddCandidateModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setActionStatus?: any;
  action: "add" | "view";
  onSave?: any;
  initialData?: CandidateModel;
  sponsorshipOptions?: {
    key: string;
    value: any;
  }[];
};
type ButtonModalProps = {
  updateCandidate: boolean;
  openModelStatus: boolean;
  viewType?: string;
  type: any;
  removeCandiate: boolean;
  addAndContinue: boolean;
  buttonLabel: string;
  creatingCandidateStatus: boolean;
  classStyle: string;
};
export default function AddCandidateModal(props: AddCandidateModalProps) {
  const {
    isOpen,
    setIsOpen,
    sponsorshipOptions,
    onSave,
    setActionStatus,
    initialData,
    action,
  } = props;

  const LocalBasePath = "page.liaison.dashboard.candidates";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });

  const { mutateAsync: addCandidate, isLoading: isCreatingCandidate } =
    useMutation(createCandidate, {
      onSuccess: (response) => {
        if (!response.isSuccess) {
          return showApiErrorMessages(response);
        }
        toast.success(t("saved"));
        onSave();
      },
    });
  const { mutateAsync: updatCandidate, isLoading: isUpdateCandidate } =
    useMutation(updateCandidateApi);
  useMutation(updateCandidateApi);
  const { mutateAsync: removeCandidate, isLoading: isRemoveCandidate } =
    useMutation(removeCandidateApi, {
      onSuccess: (response) => {
        if (!response.isSuccess) {
          return showApiErrorMessages(response);
        }
        toast.success(t("removedCandidate"));
        onSave();
      },
    });
  const getFormData = (data?: CandidateModel) => {
    return {
      sponsorship: data?.sponsorship?.id,
      motherLanguage: data?.motherLanguage,
      firstname: data?.firstName,
      middlename: data?.middleName,
      lastname: data?.lastName,
      email: data?.email,
      mobile: data?.phoneNumber,
      addAndContinue: false,
      removeCandiate: false,
      updateCandidate: false,
    };
  };

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: getFormData(initialData),
  });
  const onSubmit = (data: any) => {
    if (data.updateCandidate) {
      updateCandidateApi({
        name: `${data.firstname} ${data.middlename} ${data.lastname}`,
        first_name: data.firstname,
        middle_name: data.middlename,
        last_name: data.lastname,
        email: data.email,
        phone_number: data.mobile,
        mother_language: data.motherLanguage,
        sponsorship_id: data.sponsorship,
        id: initialData?.id,
      }).then((response: ApiResponse) => {
        if (!response.isSuccess) {
          return showApiErrorMessages(response);
        }
        setIsOpen(false);
        toast.success(t("updateCandidate"));
        onSave();
      });
    } else if (data.removeCandiate) {
      removeCandidate(initialData?.id).then((response: ApiResponse) => {
        if (!response.isSuccess) {
          return showApiErrorMessages(response);
        }
        setIsOpen(false);
      });
    } else {
      addCandidate({
        name: `${data.firstname} ${data.middlename} ${data.lastname}`,
        email: data.email,
        phone_number: data.mobile,
        first_name: data.firstname,
        middle_name: data.middlename,
        last_name: data.lastname,
        mother_language: data.motherLanguage,
        sponsorship_id: data.sponsorship,
      }).then((response: ApiResponse) => {
        if (!response.isSuccess) {
          return showApiErrorMessages(response);
        }
        if (!data.addAndContinue) setIsOpen(false);
        reset();
      });
    }
  };

  useEffect(() => {
    reset(getFormData(initialData));
  }, [isOpen, initialData]);

  const languageOptions = [
    { key: t("ar"), value: "arabic" },
    { key: t("en"), value: "english" },
  ];

  const title = {
    add: t("addNewCandidate"),
    view: t("candidacyDetails"),
    edit: t("candidacyDetailsEdit"),
  };
  const disclaimer = {
    add: t("disclaimer"),
    view: t("disclaimerDone"),
  };
  return (
    <Modal open={isOpen} setOpen={setIsOpen} >
      <Modal.Body setOpen={setIsOpen} title={title[action]}>
        <form id="addCandidate" onSubmit={handleSubmit(onSubmit)}>
          <DropDown
            id="sponsorship"
            name="sponsorship"
            schema={{ required: true }}
            disabled={action === "view"}
            register={register}
            cypressSelector={"sponsorship"}
            setValue={(value: any) =>
              setValue("sponsorship", value, { shouldValidate: true })
            }
            defaultValue={`${getValues("sponsorship")}`}
            label={t("sponsorshipRegistry")}
            placeholder={action !== "view" ? t("registryPlaceholder") : ""}
            error={!!errors.sponsorship}
            options={sponsorshipOptions || []}
          />
          <DropDown
            id="candidateLanguage"
            name="motherLanguage"
            className="mt-6"
            schema={{ required: true }}
            disabled={action === "view"}
            register={register}
            cypressSelector={"motherLanguage"}
            setValue={(value: any) =>
              setValue("motherLanguage", value, { shouldValidate: true })
            }
            defaultValue={getValues("motherLanguage")}
            label={t("motherLanguage")}
            placeholder={t("motherLanguagePlaceholder")}
            error={!!errors.motherLanguage}
            options={languageOptions}
          />
          <label className={"mt-6 block text-sm font-medium text-gray-700"}>
            {`${t("name")} *`}
          </label>
          <div className=" grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <div className="mt-1">
                <AppInput
                  hideLabel={true}
                  label={t("first")}
                  register={register}
                  error={errors.firstname}
                  scehema={{
                    required: true,
                  }}
                  disabled={action === "view"}
                  placeholder={t("first")}
                  type="text"
                  name="firstname"
                  id="firstname"
                  autoComplete="given-name"
                  className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <div className="mt-1">
                <AppInput
                  hideLabel={true}
                  label={t("father")}
                  register={register}
                  error={errors.middlename}
                  scehema={{
                    required: true,
                  }}
                  disabled={action === "view"}
                  placeholder={t("father")}
                  type="text"
                  name="middlename"
                  id="middlename"
                  autoComplete="middle-name"
                  className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="mt-1">
                <AppInput
                  hideLabel={true}
                  label={t("family")}
                  register={register}
                  error={errors.lastname}
                  scehema={{
                    required: true,
                  }}
                  disabled={action === "view"}
                  placeholder={t("family")}
                  type="text"
                  name="lastname"
                  id="lastname"
                  autoComplete="family-name"
                  className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>
          <div className="mt-6">
            <AppInput
              label={t("email")}
              register={register}
              error={errors.email}
              scehema={{
                required: true,
              }}
              disabled={action === "view"}
              placeholder={"you@example.com"}
              type="email"
              autoComplete="email"
              name="email"
              id="email"
              className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          <div className="mt-6">
            <PhoneNumberInput
              control={control}
              label={t("mobile")}
              name={"mobile"}
              placeholder={"+1 234 567 8910"}
              schema={{ required: true }}
              disabled={action === "view"}
            />
          </div>
          <div className="text-gray-500 text-sm font-normal flex justify-start mt-6">
            {disclaimer[action]}
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        {/* checking type of action add and not found initialData  show add and addAnother */}

        {action !== "view" && !initialData ? (
          <>
            <FooterModelButton
              type="submit"
              setActionStatus={setActionStatus}
              viewType={""}
              cypressSelector="add"
              classStyle="relative rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
              creatingCandidateStatus={isCreatingCandidate}
            >
              {t("add")}
            </FooterModelButton>
            <FooterModelButton
              type="submit"
              setActionStatus={setActionStatus}
              viewType={""}
              cypressSelector="addAndContinue"
              classStyle="relative mx-4 inline-flex justify-center rounded-md border border-transparent bg-brandblue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
              creatingCandidateStatus={isCreatingCandidate}
              changeValue={() => setValue("addAndContinue", true)}
            >
              {t("addAndContinue")}
            </FooterModelButton>
          </>
        ) : null}
        {/* checking type of action view show edit and delete */}
        {action === "view" && (
          <>
            <FooterModelButton
              type="button"
              viewType={"edit"}
              cypressSelector="edit"
              setActionStatus={setActionStatus}
              creatingCandidateStatus={isCreatingCandidate}
              changeValue={() => setValue("addAndContinue", false)}
              classStyle="relative rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
            >
              {t("edit")}
            </FooterModelButton>
            <FooterModelButton
              type="submit"
              viewType={"view"}
              cypressSelector="remove"
              changeValue={() => setValue("removeCandiate", true)}
              setActionStatus={setActionStatus}
              classStyle="mx-4 inline-flex justify-center rounded-md border border-transparent disabled:bg-red-600 bg-red-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              creatingCandidateStatus={isRemoveCandidate}
            >
              {t("removeCandidate")}
            </FooterModelButton>
          </>
        )}
        {/* checking type of action add and initialData found show add and cancel */}
        {action !== "view" && initialData && (
          <>
            <FooterModelButton
              type="button"
              viewType={""}
              cypressSelector="cancel"
              setActionStatus={setActionStatus}
              changeValue={() => setIsOpen(false)}
              classStyle="relative rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
              creatingCandidateStatus={isCreatingCandidate}
            >
              {t("cancel")}
            </FooterModelButton>
            <FooterModelButton
              type="submit"
              viewType={""}
              cypressSelector="updateCandidate"
              changeValue={() => setValue("updateCandidate", true)}
              setActionStatus={setActionStatus}
              classStyle="relative mx-4 inline-flex justify-center rounded-md border border-transparent bg-brandblue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
              creatingCandidateStatus={isUpdateCandidate}
            >
              {t("add")}
            </FooterModelButton>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
